import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { FINANZIERUNG_PLAN_ERROR_MELDUNG, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { Required, ValidDate } from 'src/lib/validation/ValidationTypes';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import {
	checkIfGroupsHaveNegativeValues,
	checkIfTotalsHaveNegativeValues,
	findYearsWithMissingEinnahme,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { DisplayEuro } from 'src/utils/formats';

// import schemas
import { concat } from 'lodash';
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import { FormStrucItem } from 'src/lib/types/types';
import { getAuFPruefAssistenzFormStruc, PruefAssistenzAUF } from 'src/utils/ausgabenFinanzierungHelper';
const P = Schema.attributes;

const groupPropertiesToCheck: string[] = ['ausgabe', 'einnahme', 'gesamtAusgabe', 'gesamtEinnahme', 'foerderquote', 'averageFoerderquote'];
const totalsPropertiesToCheck: string[] = [
	'gesamtausgaben',
	'summeEinnahme',
	'summeZuwendung',
	'divisionZuwendungEinnahme',
	'gesamtGesamtausgaben',
	'gesamtSummeEinnahme',
	'gesamtSummeZuwendung',
	'gesamtDivisionZuwendungEinnahme',
];
const MAX_BEANTRAGTE_FOERDERUNG = 40000;

const FinanzierungsplanStipendium: FC<{}> = () => {
	const { application } = useApplication();
	const JahrDauer = get(application, P.Allgemein_Projektdauerin_Jahren.path);
	const AllYears = Array.from({ length: JahrDauer }, (_, i) => get(application, P.Allgemein_Projektstart_Jahr.path) + i);

	const allKostenPosition = useMemo(() => {
		return get(application, P.Foerderantrag_Kostenpositions.path);
	}, [application]);

	const { groupedData: transfromedRows, totals } = transformData(allKostenPosition, JahrDauer, get(application, P.Allgemein_Projektstart_Jahr.path));

	const hasNegativeValues =
		checkIfGroupsHaveNegativeValues(transfromedRows, groupPropertiesToCheck) || checkIfTotalsHaveNegativeValues(totals, totalsPropertiesToCheck);
	const isFoerderungUeberMax = totals?.gesamtSummeZuwendung > MAX_BEANTRAGTE_FOERDERUNG ? true : false;
	const maxFoerderungMessage = `Die maximale beantragte Förderung für Ihr Vorhaben darf ${DisplayEuro(MAX_BEANTRAGTE_FOERDERUNG)} nicht überschreiten. Bitte prüfen Sie Ihre Angaben.`;

	const yearsWithMissingEinnahme = findYearsWithMissingEinnahme(totals, AllYears);
	const yearsWithMissingEinnahmeErrorMessage =
		yearsWithMissingEinnahme.length === 1 ?
			`Bitte überprüfen und korrigieren Sie Ihre Angaben: Im Jahr ${yearsWithMissingEinnahme[0]} wurden Ausgaben angegeben, aber keine Finanzierung.`
		:	`Bitte überprüfen und korrigieren Sie Ihre Angaben: In den Jahren ${yearsWithMissingEinnahme.join(', ')} wurden Ausgaben angegeben, aber keine Finanzierung.`;

	return (
		<>
			<ApplicationHeading title='Ausgaben- und Finanzierungsplan' description={PLATZHALTER_TEXT} />
			{hasNegativeValues && <MUIAlert infoType={'error'} title={'Warnung'} message={FINANZIERUNG_PLAN_ERROR_MELDUNG} />}
			{isFoerderungUeberMax && <MUIAlert infoType={'warning'} title={'Hinweis'} message={maxFoerderungMessage} />}
			{yearsWithMissingEinnahme.length > 0 && <MUIAlert infoType={'error'} title={''} message={yearsWithMissingEinnahmeErrorMessage} />}
			<TableContainer>
				<Table size='medium' aria-label='a dense table'>
					<TableHead>
						<TableRow>
							<TableCell></TableCell>
							{AllYears.map((year: any) => (
								<TableCell align='right' key={year} style={{ color: yearsWithMissingEinnahme.includes(year) ? 'red' : 'inherit' }}>
									{year}
								</TableCell>
							))}
							<TableCell align='right'>Gesamt</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Gesamtausgaben</TableCell>
							{totals.gesamtausgaben.map((value: any, index: number) => (
								<TableCell align='right' key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit' }}>
									{DisplayEuro(value.toFixed(2))}
								</TableCell>
							))}
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtGesamtausgaben) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtGesamtausgaben)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Summe Eigen- und Drittmittel</TableCell>
							{totals.summeEinnahme.map((value: any, index: number) => (
								<TableCell
									align='right'
									key={index}
									style={{
										color:
											yearsWithMissingEinnahme.includes(AllYears[index]) ? 'red'
											: parseFloat(value) < 0 ? 'red'
											: 'inherit',
									}}
								>
									{DisplayEuro(value.toFixed(2))}
								</TableCell>
							))}
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtSummeEinnahme) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtSummeEinnahme)}
							</TableCell>
						</TableRow>
						<TableRow className='total-row'>
							<TableCell sx={{ fontWeight: 'bold' }}>Beantragte Förderung</TableCell>
							{totals.summeZuwendung.map((value: any, index: number) => (
								<TableCell align='right' key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit', fontWeight: 'bold' }}>
									{DisplayEuro(value)}
								</TableCell>
							))}
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtSummeZuwendung) < 0 ? 'red' : 'inherit',
									fontWeight: 'bold',
								}}
							>
								{DisplayEuro(totals.gesamtSummeZuwendung)}
							</TableCell>
						</TableRow>
						{/* <TableRow className='total-row'>
							<TableCell sx={{ fontWeight: 'bold' }}>Beantragte Förderquote</TableCell>
							{totals.divisionZuwendungEinnahme.map((value: any, index: number) => (
								<TableCell align='right' key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit', fontWeight: 'bold' }}>
									{value > 0 ? value : 0}%
								</TableCell>
							))}
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtDivisionZuwendungEinnahme) < 0 ? 'red' : 'inherit',
									fontWeight: 'bold',
								}}
							>
								{totals.gesamtDivisionZuwendungEinnahme > 0 ? totals.gesamtDivisionZuwendungEinnahme : 0}%
							</TableCell>
						</TableRow> */}
					</TableBody>
				</Table>
			</TableContainer>
			<PruefAssistenzAUF />
		</>
	);
};

export default FinanzierungsplanStipendium;

export function transformData(data: any, JahrDauer: number, ProjektStart: number) {
	const groupedData: any = {};

	// Group by Unternehmen.id
	data?.forEach((item: any) => {
		const { attributes } = item;
		const companyId = attributes.Unternehmen?.id;
		const companyName = attributes.Unternehmen?.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation;

		if (!groupedData[companyId]) {
			groupedData[companyId] = {
				companyName,
				ausgabe: Array(JahrDauer).fill(0),
				einnahme: Array(JahrDauer).fill(0),
			};
		}

		get(attributes, 'Allgemein_Kosten_Pro_Jahr', []).forEach((jahr: any, _index: number) => {
			const yearIndex = jahr.Zelle_1 - ProjektStart; // Assuming starting year is 2024
			if (attributes.Allgemein_Kostenposition === 'Ausgabe') {
				groupedData[companyId].ausgabe[yearIndex] += parseFloat(jahr.Zelle_2) || 0;
			} else if (attributes.Allgemein_Kostenposition === 'Einnahme') {
				groupedData[companyId].einnahme[yearIndex] += parseFloat(jahr.Zelle_2) || 0;
			}
		});
	});

	// Calculate Gesamt and individuelle Förderquote
	Object.values(groupedData).forEach((group: any) => {
		group.gesamtAusgabe = group.ausgabe.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);
		group.gesamtEinnahme = group.einnahme.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);

		group.foerderquote = group.ausgabe.map((ausgabe: any, index: number) => {
			const einnahme = group.einnahme[index] || 0;
			return (((parseFloat(ausgabe) - parseFloat(einnahme)) / parseFloat(ausgabe)) * 100).toFixed(2);
		});

		// group.averageFoerderquote = (
		// 	group.foerderquote.reduce((acc: any, val: any) => acc + parseFloat(val), 0) / JahrDauer
		// ).toFixed(2);

		Object.values(groupedData).forEach((group: any) => {
			group.averageFoerderquote = (((group.gesamtAusgabe - group.gesamtEinnahme) / group.gesamtAusgabe) * 100).toFixed(2);
		});
	});
	// Calculate overall totals for the last four rows
	const totals: any = {
		gesamtausgaben: Array(JahrDauer).fill(0),
		summeEinnahme: Array(JahrDauer).fill(0),
		summeZuwendung: Array(JahrDauer).fill(0),
		divisionZuwendungEinnahme: Array(JahrDauer).fill(0),
	};

	Object.values(groupedData).forEach((group: any) => {
		group.ausgabe.forEach((value: any, index: number) => {
			totals.gesamtausgaben[index] += value;
		});
		group.einnahme.forEach((value: any, index: number) => {
			totals.summeEinnahme[index] += value;
		});
	});

	totals.summeZuwendung = totals.gesamtausgaben.map((value: any, index: number) => {
		return (value - totals.summeEinnahme[index]).toFixed(2);
	});

	totals.divisionZuwendungEinnahme = totals.summeZuwendung.map((value: any, index: number) => {
		const ausgabe = totals.gesamtausgaben[index];
		return ausgabe ? ((parseFloat(value) / ausgabe) * 100).toFixed(2) : '0.00';
	});

	totals.gesamtGesamtausgaben = totals.gesamtausgaben.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);
	totals.gesamtSummeEinnahme = totals.summeEinnahme.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);
	totals.gesamtSummeZuwendung = totals.summeZuwendung.reduce((acc: any, val: any) => acc + parseFloat(val), 0).toFixed(2);
	totals.gesamtDivisionZuwendungEinnahme = ((parseFloat(totals.gesamtSummeZuwendung) / parseFloat(totals.gesamtGesamtausgaben)) * 100).toFixed(2);

	return { groupedData, totals };
}

const formStrucAlwaysFalse = () => {
	return [
		{
			fields: [
				{
					...P.Allgemein_Projektdauerin_Jahren,
					validation: [{ validateFunction: Required }, { validateFunction: ValidDate }],
				},
			],
		},
	];
};

export const getFormStrucFinanzierungsplanStipendium = (application: any) => {
	let result: FormStrucItem[] = [];
	const JahrDauer = get(application, P.Allgemein_Projektdauerin_Jahren.path);
	const AllYears = Array.from({ length: JahrDauer }, (_, i) => get(application, P.Allgemein_Projektstart_Jahr.path) + i);

	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path);

	const { groupedData: transfromedRows, totals } = transformData(allKostenPosition, JahrDauer, get(application, P.Allgemein_Projektstart_Jahr.path));

	const hasNegativeValues =
		checkIfGroupsHaveNegativeValues(transfromedRows, groupPropertiesToCheck) || checkIfTotalsHaveNegativeValues(totals, totalsPropertiesToCheck);
	const yearsWithMissingEinnahme = findYearsWithMissingEinnahme(totals, AllYears);

	if (hasNegativeValues || yearsWithMissingEinnahme.length > 0) {
		result = concat(result, formStrucAlwaysFalse());
	}

	result = getAuFPruefAssistenzFormStruc(application, result);
	return result;
};
